requestAnimationFrame(() => {
  if (!window.popupMenuJsLoaded) {
    themeBase.onDOMContentLoadedTrigger(() => {
      const getFixedContainingBlockCandidate = (element) => {
        let current = element?.parentElement || null

        while (current) {
          const styles = window.getComputedStyle(current)
          const reasons = []

          if (styles.transform !== 'none') reasons.push(`transform:${styles.transform}`)
          if (styles.perspective !== 'none') reasons.push(`perspective:${styles.perspective}`)
          if (styles.filter !== 'none') reasons.push(`filter:${styles.filter}`)
          if (styles.backdropFilter && styles.backdropFilter !== 'none') reasons.push(`backdrop-filter:${styles.backdropFilter}`)
          if (styles.contain && styles.contain !== 'none') reasons.push(`contain:${styles.contain}`)
          if (styles.willChange && styles.willChange !== 'auto' && styles.willChange.includes('transform')) reasons.push(`will-change:${styles.willChange}`)

          if (reasons.length) {
            return {
              element: current,
              reasons
            }
          }

          current = current.parentElement
        }

        return null
      }

      const resetPopupMenuViewportCompensation = (menuWrap) => {
        if (!menuWrap) return

        menuWrap.style.removeProperty('top')
        menuWrap.style.removeProperty('left')
        menuWrap.style.removeProperty('right')
        menuWrap.style.removeProperty('bottom')
        menuWrap.style.removeProperty('width')
        menuWrap.style.removeProperty('height')
        delete menuWrap.dataset.popupMenuViewportCompensated
      }

      const syncPopupMenuViewportCompensation = (menuWrap) => {
        if (!menuWrap) return null

        const fixedContainingBlock = getFixedContainingBlockCandidate(menuWrap)

        if (!fixedContainingBlock?.element) {
          resetPopupMenuViewportCompensation(menuWrap)
          return null
        }

        const rect = fixedContainingBlock.element.getBoundingClientRect()
        const topCompensation = rect.top * -1
        const leftCompensation = rect.left * -1

        menuWrap.style.top = `${topCompensation}px`
        menuWrap.style.left = `${leftCompensation}px`
        menuWrap.style.right = 'auto'
        menuWrap.style.bottom = 'auto'
        menuWrap.style.width = `${window.innerWidth}px`
        menuWrap.style.height = 'calc(var(--window-height, 100vh) - var(--preview-bar-iframe-height))'
        menuWrap.dataset.popupMenuViewportCompensated = 'true'

        return {
          top: topCompensation,
          left: leftCompensation,
          element: fixedContainingBlock.element
        }
      }

      class MenuPopup {
        constructor(root) {
          this.root = root;
          this.button = root.querySelector('[data-popup-menu-button], .js-popup-menu-button');
          this.menuWrap = root.querySelector('[data-popup-menu-wrapper], .popup-menu-wrapper');
          this.menu = root.querySelector('[data-popup-menu-panel], .popup-menu');
          this.isValid = !!(this.button && this.menuWrap && this.menu);

          if (!this.isValid) {
            return;
          }

          this.hoveredMenuItems = [];
          this.currentHoveredMenuItem = null;
          this.subscribed = false;

          this.onWindowResize = this.onWindowResize.bind(this);
          this.onButtonClick = this.onButtonClick.bind(this);
          this.onMenuMouseOver = this.onMenuMouseOver.bind(this);
          this.onMenuMouseOut = this.onMenuMouseOut.bind(this);
          this.onMenuClick = this.onMenuClick.bind(this);
          this.onMenuWrapClick = this.onMenuWrapClick.bind(this);
          this.documentKeyPress = this.documentKeyPress.bind(this);

          this.init();
        }

        init() {
          this.button.addEventListener('click', this.onButtonClick);
          window.addEventListener('throttle30fpsResize', this.onWindowResize);
        }

        subscribeEvents() {
          if (this.subscribed) return;

          this.menu.addEventListener('mouseover', this.onMenuMouseOver);
          this.menu.addEventListener('mouseout', this.onMenuMouseOut);
          this.menu.addEventListener('click', this.onMenuClick);
          this.menuWrap.addEventListener('click', this.onMenuWrapClick);
          document.addEventListener('keydown', this.documentKeyPress);

          this.subscribed = true;
        }

        removeFromHoverMenuArr(target) {
          this.hoveredMenuItems = this.hoveredMenuItems.filter((item) => item.el !== target);
        }

        documentKeyPress(event) {
          if (event.which === 27 || event.keyCode === 27) {
            this.hide();
          }
        }

        onButtonClick() {
          this.menuWrap.classList.contains('active') ? this.hide() : this.show();
        }

        onMenuMouseOver(event) {
          const submenuItem = event.target.closest('.popup-menu__submenu-item');
          const listItem = event.target.closest('.popup-menu__list-item');
          const target = submenuItem || listItem;

          if (!target || !this.root.contains(target)) return;
          if (!submenuItem && listItem?.classList.contains('active')) return;
          if (submenuItem?.classList.contains('active')) return;

          const hasSubmenuClass = !!submenuItem;
          this.currentHoveredMenuItem = target;
          this.hoveredMenuItems.push({
            el: target,
            time: Date.now()
          });

          if (hasSubmenuClass) {
            event.stopPropagation();
          }

          clearTimeout(this.hoverTimeout);

          this.hoverTimeout = setTimeout(() => {
            const hoveredItem = this.hoveredMenuItems.find((item) => item.el === target);

            if (this.currentHoveredMenuItem === target && hoveredItem) {
              if (hasSubmenuClass) {
                this.root.querySelectorAll('.popup-menu__submenu-item').forEach((item) => item.classList.remove('active'));
                submenuItem.classList.add('active');
              } else if (listItem) {
                this.root.querySelectorAll('.popup-menu__list-item, .popup-menu__submenu-item').forEach((item) => item.classList.remove('active'));
                listItem.classList.add('active');

                const defaultSubmenuItem = listItem.querySelector('.popup-menu__submenu-item.popup-menu__has-submenu');

                if (defaultSubmenuItem) {
                  defaultSubmenuItem.classList.add('active');
                }
              }

              this.removeFromHoverMenuArr(target);
            }
          }, 150);
        }

        onMenuMouseOut(event) {
          const target = event.target.closest('.popup-menu__submenu-item, .popup-menu__list-item');

          if (!target || !this.root.contains(target)) return;
          if (event.relatedTarget && target.contains(event.relatedTarget)) return;

          const hoveredItem = this.hoveredMenuItems.find((item) => item.el === target);

          if (hoveredItem && Date.now() - hoveredItem.time < 150) {
            this.removeFromHoverMenuArr(target);
          }
        }

        onMenuClick(event) {
          if (event.target === this.menu) {
            this.hide();
          }
        }

        onMenuWrapClick(event) {
          if (event.target === this.menuWrap) {
            this.hide();
          }
        }

        show(options = {}) {
          const freezeScroll = options.freezeScroll !== false;

          (window.popupMenuInstances || []).forEach((instance) => {
            if (instance !== this) instance.hide({
              unfreezeScroll: freezeScroll
            });
          });

          this.menuWrap.classList.add('active');
          this.button.setAttribute('aria-expanded', 'true');
          this.initMenuPosition();
          this.subscribeEvents();

          if (freezeScroll) {
            themeBase.freezeScroll({
              additionalClass: 'popup-menu-opening'
            });
          }
        }

        hide(options = {}) {
          const unfreezeScroll = options.unfreezeScroll !== false;

          this.menuWrap.classList.remove('active');
          this.button.setAttribute('aria-expanded', 'false');
          this.root.querySelectorAll('.popup-menu__list-item, .popup-menu__submenu-item').forEach((item) => item.classList.remove('active'));

          if (unfreezeScroll && !document.querySelector('.popup-menu-wrapper.active')) {
            themeBase.unfreezeScroll({
              additionalClass: 'popup-menu-opening'
            });
          }

          resetPopupMenuViewportCompensation(this.menuWrap);
        }

        initMenuPosition() {
          syncPopupMenuViewportCompensation(this.menuWrap);
          this.menu.style.setProperty('--menu-align-offset-top', `${Math.floor(getLogicalRect(this.button).bottom) / 10}rem`);
        }

        onWindowResize() {
          if (this.menuWrap.classList.contains('active')) {
            this.initMenuPosition();
          }
        }
      }

      window.initPopupMenuInstances = (scope = document) => {
        const root = scope && scope.querySelectorAll ? scope : document;
        const existingInstances = Array.isArray(window.popupMenuInstances) ? window.popupMenuInstances : [];
        const connectedInstances = existingInstances.filter((instance) => instance && instance.root && instance.root.isConnected);
        const nextInstances = [...connectedInstances];
        const popupRoots = Array.from(root.querySelectorAll('.menu-popup-wrapper'));

        popupRoots.forEach((popupRoot) => {
          const existingInstance = nextInstances.find((instance) => instance.root === popupRoot);
          if (existingInstance) return;

          const popupInstance = new MenuPopup(popupRoot);
          if (popupInstance.isValid) {
            nextInstances.push(popupInstance);
          }
        });

        window.popupMenuInstances = nextInstances;
        return nextInstances;
      };

      window.initPopupMenuInstances();

      document.addEventListener('shopify:section:load', (event) => {
        window.initPopupMenuInstances(event && event.target ? event.target : document);
      });
    });
  }

  window.popupMenuJsLoaded = true;
});